import { useState } from "react";
import { CheckCircle } from "lucide-react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const tabs = [
  {
    label: "Para Empresas Iniciantes",
    subtitle: "Construa um processo sólido do zero",
    title: "Diagnóstico e Estruturação",
    description:
      "Para empresas que nunca solicitaram AFRMM ou que passaram a ter direito com a BR do Mar.",
    checklist: [
      "Análise de elegibilidade",
      "Abertura de conta vinculada (RFB)",
      "Cadastramento ANTAQ e Sistema Mercante",
      "Pedido de creditamento via COMPROT",
      "Aprovação e acompanhamento no BNDES",
      "Treinamento da equipe operacional",
    ],
    result:
      "Resultado: você sai do zero para o nível das empresas de referência no setor.",
    cta: "Solicitar Análise Gratuita →",
  },
  {
    label: "Para Empresas Experientes",
    subtitle: "Eficiência e tecnologia para quem já opera",
    title: "Automação e Eficiência Operacional",
    description:
      "Para empresas que já solicitam AFRMM mas querem escalar com tecnologia e menos retrabalho.",
    checklist: [
      "Auditoria dos lançamentos no Sistema Mercante",
      "Identificação de créditos não aproveitados",
      "Automação proprietária SIATECH no seu processo",
      "Redução de operações manuais e repetitivas",
      "Gestão de travas regulatórias e pendências",
      "Acompanhamento contínuo via COMPROT",
    ],
    result:
      "Resultado: o que você já faz, agora feito em minutos — sem erros e com mais aprovações.",
    cta: "Quero otimizar meu processo →",
  },
  {
    label: "Serviços Contínuos",
    subtitle: "Relacionamento e suporte de longo prazo",
    title: "Gestão e Acompanhamento Especializado",
    description:
      "Para empresas que querem um parceiro permanente, não apenas um serviço pontual.",
    checklist: [
      "Monitoramento contínuo do processo AFRMM",
      "Resolução de travas com base em experiência acumulada",
      "Gestão de projetos técnicos especializados",
      "Monitoramento de mudanças regulatórias",
      "Relatórios periódicos de performance",
      "Suporte dedicado com especialistas AFRMM",
    ],
    result:
      "Resultado: sua operação AFRMM sempre no ponto — com quem conhece todos os caminhos.",
    cta: "Falar com um especialista →",
  },
];

const Services = () => {
  const [activeTab, setActiveTab] = useState(0);
  const tab = tabs[activeTab];
  const sectionRef = useScrollReveal();

  return (
    <section
      id="servicos"
      ref={sectionRef as React.RefObject<HTMLElement>}
      className="py-24 relative overflow-hidden bg-grid"
      style={{ background: "var(--color-carvao)" }}
    >
      <div className="container mx-auto px-4 lg:px-6 relative">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12" data-animate>
          <div className="eyebrow justify-center" style={{ color: "var(--color-cinza-ancora)" }}>
            Serviços
          </div>
          <h2
            className="heading-h2 mb-4"
            style={{ color: "var(--color-branco-vela)" }}
          >
            Para onde sua empresa está — temos o serviço certo.
          </h2>
          <div
            className="w-14 h-px mx-auto mb-6"
            style={{ background: "var(--color-cinza-ancora)", opacity: 0.3 }}
          />
          <p
            className="text-base font-light leading-relaxed"
            style={{ color: "var(--color-cinza-nevoa)" }}
          >
            Atendemos desde quem nunca tocou em AFRMM até empresas que já operam
            e precisam evoluir.
          </p>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap justify-center gap-2 md:gap-0 mb-10" data-animate>
          {tabs.map((t, index) => (
            <button
              key={index}
              onClick={() => setActiveTab(index)}
              className="px-6 py-3 text-sm font-medium transition-all duration-200 border-b-2 cursor-pointer"
              style={{
                color: activeTab === index ? "var(--color-branco-vela)" : "var(--color-cinza-ancora)",
                borderBottomColor: activeTab === index ? "var(--color-dourado)" : "transparent",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="max-w-2xl mx-auto" data-animate>
          <div
            className="p-8 md:p-10 rounded"
            style={{
              background: "rgba(10, 10, 10, 0.5)",
              border: "1px solid rgba(176, 175, 172, 0.15)",
            }}
          >
            <p
              className="italic text-sm mb-2 font-light"
              style={{ color: "var(--color-cinza-ancora)" }}
            >
              {tab.subtitle}
            </p>
            <h3
              className="font-bold text-2xl mb-4 tracking-tight"
              style={{ color: "var(--color-branco-vela)" }}
            >
              {tab.title}
            </h3>
            <p
              className="text-[15px] leading-relaxed mb-6 font-light"
              style={{ color: "var(--color-cinza-ancora)" }}
            >
              {tab.description}
            </p>

            {/* Checklist */}
            <div className="space-y-3 mb-6">
              {tab.checklist.map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span
                    className="text-xs flex-shrink-0"
                    style={{ color: "var(--color-dourado)" }}
                  >
                    •
                  </span>
                  <span
                    className="text-sm font-light"
                    style={{ color: "var(--color-cinza-nevoa)" }}
                  >
                    {item}
                  </span>
                </div>
              ))}
            </div>

            <p
              className="font-medium text-base mb-6"
              style={{ color: "var(--color-branco-vela)" }}
            >
              {tab.result}
            </p>

            <a
              href="#contato"
              className="btn-cta-dark text-sm"
            >
              {tab.cta}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
